import { useState } from 'react';
import { TiArrowSortedUp } from 'react-icons/ti';

export default function ContactForm() {
  const [isDropdownProject, setIsDropdownProject] = useState(false);
  const [selectedOptionProject, setSelectedOptionProject] = useState(
    'Project Budget (Min $2k)'
  );
  const [isDropdownRequirement, setIsDropdownRequirement] = useState(false);
  const [selectedOptionRequirement, setSelectedOptionRequirement] = useState(
    'Select your requirement'
  );

  const [formValues, setFormValues] = useState({
    name: '',
    companyName: '',
    email: '',
    phone: '',
    subject: '',
    description: '',
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const requirements = [
    'UX/UI',
    'Landing Page',
    'Website Development',
    'Custom Software Development',
    'Performance Marketing',
    'Consulting',
    'Collaboration',
  ];

  const toggleDropdownProject = () => setIsDropdownProject(!isDropdownProject);
  const toggleDropdownRequirement = () =>
    setIsDropdownRequirement(!isDropdownRequirement);

  const handleOptionProject = option => {
    setSelectedOptionProject(option);
    setIsDropdownProject(false);
  };

  const handleOptionRequirement = option => {
    setSelectedOptionRequirement(option);
    setIsDropdownRequirement(false);
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const validateForm = () => {
    const errors = {};
    Object.keys(formValues).forEach(key => {
      if (!formValues[key]) errors[key] = true;
    });
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    const scriptUrl =
      'https://script.google.com/macros/s/AKfycby2UIShZvPzcb6UuylrTFaEEcY166OIMdVxvj0D1GLGXQ6a-G74Wi-mk9xHhG_alH8Z/exec';

    try {
      const formData = new FormData();
      formData.append('name', formValues.name);
      formData.append('companyName', formValues.companyName);
      formData.append('email', formValues.email);
      formData.append('phone', formValues.phone);
      formData.append('subject', formValues.subject);
      formData.append('description', formValues.description);
      formData.append('requirement', selectedOptionRequirement);
      formData.append('project_budget', selectedOptionProject);

      await fetch(scriptUrl, {
        method: 'POST',
        mode: 'no-cors',
        body: formData,
      });

      // Because of no-cors, we assume success if the fetch doesn't throw
      setStatus({
        type: 'success',
        message: 'Message sent successfully! We will get back to you soon.',
      });

      // Reset form
      setFormValues({
        name: '',
        companyName: '',
        email: '',
        phone: '',
        subject: '',
        description: '',
      });
      setSelectedOptionProject('Project Budget (Min $2k)');
      setSelectedOptionRequirement('Select your requirement');
      setValidationErrors({});
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Something went wrong. Please try again later.',
      });
      console.error('Submission Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="flex flex-col gap-y-2 mx-auto" onSubmit={handleSubmit}>
      {/* Name Field */}
      <input
        className={`border-[1px] ${validationErrors.name ? 'border-red-500' : 'border-borderColor'} text-xs pl-5 bg-bgInput text-white w-full rounded-[15px] p-2`}
        placeholder="Name"
        name="name"
        value={formValues.name}
        onChange={handleInputChange}
      />

      {/* Company & Email */}
      <div className="flex gap-x-4">
        <input
          className={`border-[1px] ${validationErrors.companyName ? 'border-red-500' : 'border-borderColor'} text-xs pl-5 bg-bgInput text-white w-1/2 rounded-[15px] p-2`}
          placeholder="Company name"
          name="companyName"
          value={formValues.companyName}
          onChange={handleInputChange}
        />
        <input
          className={`border-[1px] ${validationErrors.email ? 'border-red-500' : 'border-borderColor'} text-xs pl-5 bg-bgInput text-white w-1/2 rounded-[15px] p-2`}
          placeholder="Email"
          name="email"
          value={formValues.email}
          onChange={handleInputChange}
        />
      </div>

      {/* Phone & Subject */}
      <div className="flex gap-x-4">
        <input
          className={`border-[1px] ${validationErrors.phone ? 'border-red-500' : 'border-borderColor'} text-xs pl-5 bg-bgInput text-white w-1/2 rounded-[15px] p-2`}
          placeholder="Phone"
          name="phone"
          value={formValues.phone}
          onChange={handleInputChange}
        />
        <input
          className={`border-[1px] ${validationErrors.subject ? 'border-red-500' : 'border-borderColor'} text-xs pl-5 bg-bgInput text-white w-1/2 rounded-[15px] p-2`}
          placeholder="Subject"
          name="subject"
          value={formValues.subject}
          onChange={handleInputChange}
        />
      </div>

      {/* Requirement Dropdown */}
      <div className="relative z-[200]">
        <div
          className="absolute text-white text-xs pl-5 bg-bgInput rounded-[15px] border-[1px] border-borderColor p-2 w-full cursor-pointer"
          onClick={toggleDropdownRequirement}
        >
          <div className="flex items-center text-textColor2 justify-between">
            {selectedOptionRequirement}
            <TiArrowSortedUp
              className={isDropdownRequirement ? '' : 'rotate-180'}
            />
          </div>
          {isDropdownRequirement && (
            <div className="mt-2 z-[100] bg-bgInput border border-borderColor rounded-md overflow-hidden">
              {requirements.map(req => (
                <div
                  key={req}
                  className="text-textColor2 py-2 px-5 hover:bg-primary/20 cursor-pointer bg-bgInput"
                  onClick={() => handleOptionRequirement(req)}
                >
                  {req}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Description Field */}
      <textarea
        rows={1}
        placeholder="Describe your requirement"
        className={`text-white bg-bgInput mt-8 text-xs pl-4 rounded-[15px] border-[1px] ${validationErrors.description ? 'border-red-500' : 'border-borderColor'} p-2 w-full lg:rows-[4]`}
        name="description"
        value={formValues.description}
        onChange={handleInputChange}
      />

      {/* Budget Dropdown */}
      <div className="relative z-[150]">
        <div
          className="absolute text-white text-xs pl-5 bg-bgInput rounded-[15px] border-[1px] border-borderColor p-2 w-full cursor-pointer"
          onClick={toggleDropdownProject}
        >
          <div className="flex items-center text-textColor2 justify-between">
            {selectedOptionProject}
            <TiArrowSortedUp
              className={isDropdownProject ? '' : 'rotate-180'}
            />
          </div>
          {isDropdownProject && (
            <div className="mt-2 z-[150] bg-bgInput border border-borderColor rounded-md overflow-hidden">
              {['$2000-$5000', '$5000-$8000', '$8000-$15000'].map(budget => (
                <div
                  key={budget}
                  className="text-textColor2 py-2 px-5 hover:bg-primary/20 cursor-pointer bg-bgInput"
                  onClick={() => handleOptionProject(budget)}
                >
                  {budget}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Success/Error Message */}
      {status.message && (
        <div
          className={`mt-12 p-3 rounded-[10px] text-xs text-center border ${
            status.type === 'success'
              ? 'bg-green-500/10 border-green-500 text-green-400'
              : 'bg-red-500/10 border-red-500 text-red-400'
          }`}
        >
          {status.message}
        </div>
      )}

      {/* Footer & Submit */}
      <div
        className={`flex flex-col items-center gap-y-2 lg:flex-row lg:gap-x-2 ${status.message ? 'mt-4' : 'mt-12'}`}
      >
        <p className="text-white text-[0.75em] font-lato">
          Please be informed that when you click the Send button BuildAtScale
          will process your personal data in accordance with our{' '}
          <a className="underline cursor-pointer">Privacy Policy</a>.
        </p>
        <button
          type="submit"
          disabled={isSubmitting}
          className={`bg-primary max-w-[8em] font-lato text-[0.75em] font-bold text-white w-full rounded-full p-2 transition-all ${
            isSubmitting
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:brightness-110 active:scale-95'
          }`}
        >
          {isSubmitting ? 'Sending...' : 'Submit'}
        </button>
      </div>
    </form>
  );
}
